import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import GavelIcon from '@mui/icons-material/Gavel'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { Grid } from '@mui/material'

import {
  COLOR_FIRST_BID,
  COLOR_FIRST_COMPLETED_JOB,
  DEFAULT_FIRST_BID_THRESHOLD_DAYS,
  DEFAULT_FIRST_COMPLETED_JOB_THRESHOLD_DAYS,
} from 'components/reports/Metrics/constants/SupplierActivation'
import { SupplierActivationDto } from 'generated/graphql'
import {
  days,
  eligibleCaption,
  participationCaption,
  percentage,
} from 'helpers/supplierActivation.helpers'

import { SupplierActivationKpiCard } from './SupplierActivationKpiCard'

interface Props {
  data?: SupplierActivationDto | null
  loading?: boolean
}

export const SupplierActivationCards = ({ data, loading }: Props) => {
  const cohortSize = data?.cohortSize ?? 0
  const firstBid = data?.firstBid
  const firstCompletedJob = data?.firstCompletedJob

  // Falls back on nought as well as null: a threshold of nought days is never a real setting, so it
  // would only ever reach the titles as "within 0 days".
  const bidDays = firstBid?.headlineDayThreshold || DEFAULT_FIRST_BID_THRESHOLD_DAYS
  const jobDays =
    firstCompletedJob?.headlineDayThreshold || DEFAULT_FIRST_COMPLETED_JOB_THRESHOLD_DAYS

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={4} xl>
        <SupplierActivationKpiCard
          title='Newly created drivers'
          value={cohortSize.toLocaleString()}
          color='#7B61FF'
          icon={<PeopleAltOutlinedIcon />}
          caption='100% of cohort'
          info='Drivers with the Owner or Transport Controller role whose OnePoint portal account was created during the selected period.'
          loading={loading}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} xl>
        <SupplierActivationKpiCard
          title={`First bid within ${bidDays} days`}
          value={percentage(firstBid?.headlinePercentage)}
          color={COLOR_FIRST_BID}
          icon={<GavelIcon />}
          caption={eligibleCaption(firstBid)}
          subCaption={`Drivers with at least ${bidDays} days since creation`}
          info={`Drivers are included once at least ${bidDays} days have passed since their account creation. Drivers whose accounts were created less than ${bidDays} days ago are excluded, as their ${bidDays}-day period has not yet been completed.`}
          loading={loading}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} xl>
        <SupplierActivationKpiCard
          title={`First completed job within ${jobDays} days`}
          value={percentage(firstCompletedJob?.headlinePercentage)}
          color={COLOR_FIRST_COMPLETED_JOB}
          icon={<CheckCircleOutlineIcon />}
          caption={eligibleCaption(firstCompletedJob)}
          subCaption={`Drivers with at least ${jobDays} days since creation`}
          info={`Only drivers whose accounts were created at least ${jobDays} days ago are included. More recently created accounts are excluded because their ${jobDays}-day period has not yet been completed.`}
          loading={loading}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} xl>
        <SupplierActivationKpiCard
          title='Median time to first bid'
          value={days(firstBid?.medianDays)}
          color={COLOR_FIRST_BID}
          icon={<ScheduleIcon />}
          caption={participationCaption(firstBid, cohortSize, 'have bid')}
          info='The typical timeframe between a driver’s account creation and placing their first bid. Half of the drivers placed their first bid within this timeframe, and half took longer. Drivers who have not placed a bid are excluded.'
          loading={loading}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4} xl>
        <SupplierActivationKpiCard
          title='Median time to first completed job'
          value={days(firstCompletedJob?.medianDays)}
          color={COLOR_FIRST_COMPLETED_JOB}
          icon={<ScheduleIcon />}
          caption={participationCaption(firstCompletedJob, cohortSize, 'have completed a job')}
          info='The typical timeframe between a driver’s account creation and completing their first job. Half of the drivers completed their first job within this timeframe, and half took longer. Drivers who have not completed a job are excluded.'
          loading={loading}
        />
      </Grid>
    </Grid>
  )
}
